import { useState } from 'react';
import { FeatureFlag } from '../types/community';

/**
 * Feature Flags Hook
 * Manages feature rollouts and A/B testing
 * In production, integrate with LaunchDarkly, Unleash, or Supabase config
 */

// Default feature flags configuration
const DEFAULT_FLAGS: Record<string, FeatureFlag> = {
  reviews: {
    key: 'reviews',
    enabled: true,
    description: 'Write and view reviews',
  },
  photo_upload: {
    key: 'photo_upload',
    enabled: true,
    description: 'Upload photos to parks',
  },
  meetups: {
    key: 'meetups',
    enabled: true,
    description: 'Create and join meetups',
  },
  walking_buddy: {
    key: 'walking_buddy',
    enabled: true,
    description: 'Walking buddy matching system',
  },
  messaging: {
    key: 'messaging',
    enabled: true,
    description: 'Direct messaging between users',
  },
  weather_widget: {
    key: 'weather_widget',
    enabled: true,
    rollout_percentage: 100,
    description: 'Weather information on park pages',
  },
  offline_mode: {
    key: 'offline_mode',
    enabled: true,
    description: 'Offline data access',
  },
  push_notifications: {
    key: 'push_notifications',
    enabled: true,
    description: 'Push notification support',
  },
  user_profiles: {
    key: 'user_profiles',
    enabled: true,
    description: 'Public user profile pages',
  },
  report_content: {
    key: 'report_content',
    enabled: true,
    description: 'Report inappropriate content',
  },
  helpful_votes: {
    key: 'helpful_votes',
    enabled: true,
    description: 'Vote on helpful reviews',
  },
  advanced_filters: {
    key: 'advanced_filters',
    enabled: true,
    description: 'Advanced park filtering',
  },
  // Future features (disabled by default)
  ai_recommendations: {
    key: 'ai_recommendations',
    enabled: false,
    rollout_percentage: 0,
    description: 'AI-powered park recommendations',
  },
  premium_features: {
    key: 'premium_features',
    enabled: false,
    rollout_percentage: 0,
    description: 'Premium subscription features',
  },
  social_sharing: {
    key: 'social_sharing',
    enabled: true,
    description: 'Share to social media',
  },
};

class FeatureFlagService {
  private flags: Record<string, FeatureFlag>;
  private userId?: string;

  constructor() {
    this.flags = this.loadFlags();
  }

  private loadFlags(): Record<string, FeatureFlag> {
    try {
      const stored = localStorage.getItem('feature_flags');
      if (stored) {
        return { ...DEFAULT_FLAGS, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error('Failed to load feature flags:', error);
    }
    return DEFAULT_FLAGS;
  }

  private saveFlags() {
    try {
      localStorage.setItem('feature_flags', JSON.stringify(this.flags));
    } catch (error) {
      console.error('Failed to save feature flags:', error);
    }
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  isEnabled(flagKey: string): boolean {
    const flag = this.flags[flagKey];
    if (!flag) {
      console.warn(`Feature flag '${flagKey}' not found`);
      return false;
    }

    if (!flag.enabled) {
      return false;
    }

    // Check rollout percentage
    if (
      flag.rollout_percentage !== undefined &&
      flag.rollout_percentage < 100
    ) {
      return this.shouldShowForUser(flagKey, flag.rollout_percentage);
    }

    return true;
  }

  private shouldShowForUser(flagKey: string, percentage: number): boolean {
    // Deterministic rollout based on user ID
    // Users will consistently see the same variant
    if (!this.userId) {
      // For logged-out users, use random
      return Math.random() * 100 < percentage;
    }

    // Simple hash function for deterministic rollout
    const hash = this.hashString(this.userId + flagKey);
    return hash % 100 < percentage;
  }

  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  getFlag(flagKey: string): FeatureFlag | undefined {
    return this.flags[flagKey];
  }

  getAllFlags(): FeatureFlag[] {
    return Object.values(this.flags);
  }

  // Admin functions (for testing/debugging)
  overrideFlag(flagKey: string, enabled: boolean) {
    if (this.flags[flagKey]) {
      this.flags[flagKey].enabled = enabled;
      this.saveFlags();
    }
  }

  resetFlags() {
    this.flags = { ...DEFAULT_FLAGS };
    this.saveFlags();
  }

  // Variant testing (A/B testing)
  getVariant(experimentKey: string, variants: string[]): string {
    if (!this.userId) {
      return variants[0];
    }

    const hash = this.hashString(this.userId + experimentKey);
    const index = hash % variants.length;
    return variants[index];
  }
}

const featureFlagService = new FeatureFlagService();

export const useFeatureFlags = () => {
  const [flags, setFlags] = useState<FeatureFlag[]>(
    featureFlagService.getAllFlags()
  );

  const isEnabled = (flagKey: string): boolean => {
    return featureFlagService.isEnabled(flagKey);
  };

  const getFlag = (flagKey: string): FeatureFlag | undefined => {
    return featureFlagService.getFlag(flagKey);
  };

  const setUserId = (userId: string) => {
    featureFlagService.setUserId(userId);
  };

  const getVariant = (experimentKey: string, variants: string[]): string => {
    return featureFlagService.getVariant(experimentKey, variants);
  };

  // Admin functions
  const overrideFlag = (flagKey: string, enabled: boolean) => {
    featureFlagService.overrideFlag(flagKey, enabled);
    setFlags(featureFlagService.getAllFlags());
  };

  const resetFlags = () => {
    featureFlagService.resetFlags();
    setFlags(featureFlagService.getAllFlags());
  };

  return {
    isEnabled,
    getFlag,
    setUserId,
    getVariant,
    flags,
    overrideFlag,
    resetFlags,
  };
};

// Export service for direct access
export { featureFlagService };

// Convenience hook for single feature
export const useFeature = (flagKey: string): boolean => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flagKey);
};
